import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import * as mcquizApi from '../services/mcquizApi';

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function BarChart({ data, label }: { data: { bucket: string; count: number }[]; label: string }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <div className="space-y-1.5">
        {data.map((d) => (
          <div key={d.bucket} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-16 text-right shrink-0">
              {d.bucket}
            </span>
            <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded transition-all"
                style={{ width: `${(d.count / max) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300 w-8">{d.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function McQuizAnalytics() {
  const [searchParams] = useSearchParams();
  const preselectedTestId = searchParams.get('test_id');
  const [selectedTestId, setSelectedTestId] = useState<string>(preselectedTestId || '');

  // Overview
  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['mcquiz-analytics-overview'],
    queryFn: () => mcquizApi.getOverviewAnalytics().then((r) => r.data),
  });

  // Test list for dropdown
  const { data: testsData } = useQuery({
    queryKey: ['mcquiz-tests-all'],
    queryFn: () => mcquizApi.listTests().then((r) => r.data),
  });
  const tests: any[] = testsData?.tests ?? testsData ?? [];

  // Per-test analytics
  const { data: testAnalytics, isLoading: loadingTestAnalytics } = useQuery({
    queryKey: ['mcquiz-test-analytics', selectedTestId],
    queryFn: () => mcquizApi.getTestAnalytics(Number(selectedTestId)).then((r) => r.data),
    enabled: !!selectedTestId,
  });

  const overviewData = overview ?? {};
  const testData = testAnalytics ?? {};

  // Build score distribution chart data
  const distribution: { bucket: string; count: number }[] =
    testData.score_distribution ??
    (testData.scores
      ? [
          { bucket: '0-20%', count: 0 },
          { bucket: '21-40%', count: 0 },
          { bucket: '41-60%', count: 0 },
          { bucket: '61-80%', count: 0 },
          { bucket: '81-100%', count: 0 },
        ]
      : []);

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <ChartBarIcon className="w-7 h-7 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">MCQuiz Analytics</h1>
      </div>

      {/* Overview cards */}
      {loadingOverview ? (
        <div className="flex justify-center py-12">
          <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Tests"
            value={overviewData.total_tests ?? 0}
          />
          <StatCard
            label="Total Questions"
            value={overviewData.total_questions ?? 0}
          />
          <StatCard
            label="Total Sessions"
            value={overviewData.total_sessions ?? 0}
          />
          <StatCard
            label="Avg Score"
            value={
              overviewData.avg_score !== undefined
                ? `${Number(overviewData.avg_score).toFixed(1)}%`
                : '-'
            }
          />
        </div>
      )}

      {/* Per-test analytics */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Test Performance</h2>
          <select
            value={selectedTestId}
            onChange={(e) => setSelectedTestId(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm min-w-[240px]"
          >
            <option value="">Select a test</option>
            {tests.map((t: any) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>

        {!selectedTestId ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
            Select a test above to view detailed analytics.
          </p>
        ) : loadingTestAnalytics ? (
          <div className="flex justify-center py-12">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Attempts" value={testData.attempts ?? 0} />
              <StatCard
                label="Avg Score"
                value={
                  testData.avg_score !== undefined
                    ? `${Number(testData.avg_score).toFixed(1)}%`
                    : '-'
                }
              />
              <StatCard
                label="Max Score"
                value={
                  testData.max_score !== undefined
                    ? `${Number(testData.max_score).toFixed(1)}%`
                    : '-'
                }
              />
              <StatCard
                label="Min Score"
                value={
                  testData.min_score !== undefined
                    ? `${Number(testData.min_score).toFixed(1)}%`
                    : '-'
                }
              />
            </div>

            {distribution.length > 0 && (
              <BarChart data={distribution} label="Score Distribution" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
